// ============================================================================
// Per-intervention "themes" — the theme-specific copy that the template builder
// interpolates into the shared 14-node branching structure (task-3 through
// task-8 plus A, A1, B, C, D, E, F, G, H, I, K, L, M, N).
//
// Only task-1 (Getting Started), task-2 (initial assessment), and task-9
// (completion) use the existing content from interventionTasks.js.
// ============================================================================

const THEMES = {
  quiet_the_worry: {
    theme: "the worry",
    t3: {
      opener: "You're here — that's already a win! Worry loses its power the moment you start turning toward it.",
      bodyResponse: "your chest feeling tight, your stomach knotting, your breathing becoming shallow, or your muscles tensing up",
      systemNote: "Worry keeps your body stuck in a low-grade alarm, even when nothing urgent is actually happening.",
      emotionalDetail: "the rumination, the what-ifs, or the restless feeling that you can't quite settle",
      bodyTension: "tight chest, knotted stomach, shallow breath, clenched jaw",
      emotionalList: "rumination, dread, restlessness, fear",
    },
    t4: {
      subject: "the worry itself",
      storyIntro: "Bring up the worry in detail: the situation you keep turning over, the what-ifs looping in your head, the outcomes you fear. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the worry story",
    },
    t5: {
      gripPhrase: "loosen more of the grip worry has on you",
      storyReplay: "**replay** the worry story from beginning to end",
      followup: "At this point, your worry may feel lighter this time.",
    },
    t6: {
      contextQuestion: "Does the worry feel lighter now? Does the situation feel less threatening? Does your body stay calmer when you picture it?",
      positiveVision: "Imagine yourself facing that same situation with calm, clear-headed confidence — problem-solving with ease, trusting yourself, letting the what-ifs drift by without latching on. Maybe you're surrounded by a gentle protective light, or you feel grounded and strong no matter what comes. Paint this new picture as vividly as you can — you're the artist here.",
    },
    t7: {
      transformations: "Every what-if becomes a steady thought, every knot in your stomach softens into calm, every sleepless worry becomes quiet confidence.",
      metaphor: "just like replacing old stained carpet with a beautiful, pristine new wood floor",
    },
    t8: {
      triggerExamples: "a difficult conversation on the horizon, a news headline, a quiet moment when your mind starts to spin, a reminder on your calendar",
    },
  },

  overcome_overwhelm: {
    theme: "the overwhelm",
    t3: {
      opener: "Look at you, showing up for yourself! When it all feels like too much, starting anywhere is everything.",
      bodyResponse: "your chest feeling heavy, your breath short and shallow, a tightness behind your eyes, or a restless buzz in your limbs",
      systemNote: "Overwhelm floods your body with stress signals even when there isn't a single threat to face.",
      emotionalDetail: "the sense of having too much to hold, the urge to shut down, or the fear of dropping something important",
      bodyTension: "heavy chest, shallow breath, tight shoulders, restless limbs",
      emotionalList: "overload, urgency, frustration, fear",
    },
    t4: {
      subject: "what's overwhelming you",
      storyIntro: "Bring up the overwhelm in detail: the pile of responsibilities, the competing demands, the feeling of being pulled in every direction at once. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the overwhelm story",
    },
    t5: {
      gripPhrase: "loosen more of the squeeze overwhelm has on your chest and mind",
      storyReplay: "**replay** the overwhelm from beginning to end",
      followup: "At this point, the pile may feel smaller and more manageable this time.",
    },
    t6: {
      contextQuestion: "Does the pile feel lighter now? Does the chaos feel a little quieter? Does your body stay calmer when you picture it all?",
      positiveVision: "Imagine yourself moving through your day with unhurried clarity — taking one thing at a time, saying no when you need to, noticing space between moments. Maybe you feel steadier or a lightness in your chest, or you see yourself gliding calmly through the to-do list as if you have all the time you need. Let this new picture be as spacious as you need it to be.",
    },
    t7: {
      transformations: "Every racing thought becomes a clear next step, every tight chest becomes an easy breath, every crushing pile becomes a manageable list.",
      metaphor: "just like sweeping clutter off a desk until a clean workspace shines through",
    },
    t8: {
      triggerExamples: "opening your inbox, looking at your calendar, a deadline approaching, the moment someone adds one more thing to your plate",
    },
  },

  dial_down_tension: {
    theme: "the body tension",
    t3: {
      opener: "Great move! Your body has been carrying a lot — let's give it some overdue relief.",
      bodyResponse: "your shoulders hiked up, your jaw clenched, your fists curled, or a bracing tightness running through your chest and belly",
      systemNote: "The body holds onto tension long after the original stressor is gone — a kind of muscular memory.",
      emotionalDetail: "the frustration, the edginess, or the feeling of being wound too tight",
      bodyTension: "raised shoulders, clenched jaw, tight chest, tense lower back",
      emotionalList: "frustration, irritation, edginess, restlessness",
    },
    t4: {
      subject: "the situation holding that tension in place",
      storyIntro: "Bring up the situation that's keeping your body braced — whether it's a specific stressor, a chronic pressure, or just a build-up of daily wear. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the tension story",
    },
    t5: {
      gripPhrase: "unlock more of the places your body has been holding on tight",
      storyReplay: "**replay** the tension-holding situation from beginning to end",
      followup: "At this point, your body may feel softer and less tight this time.",
    },
    t6: {
      contextQuestion: "Is your body a little softer now? Does the situation feel less bracing? Can you let your shoulders drop?",
      positiveVision: "Imagine your body completely at ease — shoulders loose, jaw soft, breath flowing deep into your belly. Picture yourself moving through your day carrying no armor, meeting moments with a relaxed, open body. Maybe you feel warm, liquid ease pouring through your muscles, or you see yourself moving with the grace of water. Let your imagination soften you further — go as easy and slow as feels right.",
    },
    t7: {
      transformations: "Every clenched muscle softens, every braced breath becomes easy, every tight corner of your body becomes a place of rest.",
      metaphor: "just like tight guitar strings loosening back into perfect, easy tuning",
    },
    t8: {
      triggerExamples: "waking up with a stiff neck, sitting at your desk for hours, a stressful email, the late afternoon slump when everything tightens",
    },
  },

  healing_old_stories: {
    theme: "the old story",
    t3: {
      opener: "This takes real courage, and you're bringing it! Old stories soften the moment we're finally ready to tend to them.",
      bodyResponse: "a heavy feeling in your chest, a lump in your throat, a sinking feeling in your gut, or a pressure behind your eyes",
      systemNote: "Old memories live in the body long after the event itself has passed — carried as sensations rather than words.",
      emotionalDetail: "the old hurt, the familiar sadness, or the feeling of being pulled back into something you thought was long gone",
      bodyTension: "heavy chest, throat lump, sinking gut, tight shoulders",
      emotionalList: "sadness, anger, shame, grief",
    },
    t4: {
      subject: "the old memory itself",
      storyIntro: "Bring up the old memory in detail: where you were, what was happening, what you felt then. Let it replay like a story or a scene in a movie. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the old memory",
    },
    t5: {
      gripPhrase: "loosen more of the hold the old memory has on you today",
      storyReplay: "**replay** the old memory from beginning to end",
      followup: "At this point, the memory may feel more distant and less charged this time.",
    },
    t6: {
      contextQuestion: "Does the memory feel more distant now? Does its weight feel lighter? Is your body quieter when you picture it?",
      positiveVision: "Imagine yourself meeting that younger you with love and compassion. Picture the safety, understanding, or care you needed back then, given to you now. Perhaps you see the story play out a different, more positive way. Maybe you see a warm, protective light around you in that earlier time, or you simply sit beside them and let them know it's all okay now. Let yourself imagine this in whatever way brings the most healing.",
    },
    t7: {
      transformations: "Every old hurt becomes a tender acknowledgment, every painful scene becomes a gentler memory, every old story becomes part of your past — no longer running today.",
      metaphor: "just like softening a sharp old photograph into a gentler, faded picture in an album",
    },
    t8: {
      triggerExamples: "a song on the radio, seeing someone who looks like a person from the past, a familiar place, a certain date on the calendar",
    },
  },

  fear_of_sleeplessness: {
    theme: "the worry about sleep",
    t3: {
      opener: "Good for you for facing this head-on! Better sleep is closer than you think.",
      bodyResponse: "your mind whirring the moment your head hits the pillow, your chest tightening at the thought of another sleepless night, or a jittery wakefulness just when you should be settling down",
      systemNote: "The fear of not sleeping often becomes the very thing that prevents sleep — a loop your body learned.",
      emotionalDetail: "the dread of the hours ahead, the frustration at your own alert mind, or the despair of another exhausting tomorrow",
      bodyTension: "racing mind, tight chest, jittery body, shallow breath",
      emotionalList: "frustration, dread, worry, fatigue",
    },
    t4: {
      subject: "the worry about sleep",
      storyIntro: "Bring up the whole sleep story: getting into bed, watching the clock, checking the time at 2am, 4am, feeling the morning approach with no rest behind you. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the sleep story",
    },
    t5: {
      gripPhrase: "weaken more of the grip sleeplessness has on your nights",
      storyReplay: "**replay** the sleep story from beginning to end",
      followup: "At this point, the sleep story may feel less frightening this time.",
    },
    t6: {
      contextQuestion: "Does bedtime feel less bothersome now? Does the sleep situation feel less frightening? Does your body soften when you picture it?",
      positiveVision: "Imagine yourself climbing into bed with complete ease — your body heavy and warm, your breath slowing, your mind drifting without effort. Picture yourself sinking into deep, restorative sleep and waking refreshed. Maybe the room is bathed in soft moonlight, or you feel as if you're floating gently downward into rest. Let the picture settle over you as softly as sleep itself.",
    },
    t7: {
      transformations: "Every racing bedtime thought becomes a drifting cloud, every clock check becomes a soft turning-over, every fear of sleeplessness becomes quiet, easy rest.",
      metaphor: "just like replacing a thin, scratchy blanket with a soft, heavy quilt",
    },
    t8: {
      triggerExamples: "it's nearly bedtime, you're brushing your teeth, you see the clock on the nightstand, you wake in the middle of the night",
    },
  },

  shake_off_sadness: {
    theme: "the blues",
    t3: {
      opener: "The blues are heavy, but your willingness to show up today is already light breaking in.",
      bodyResponse: "a heaviness in your chest, tiredness in your limbs, a lump in your throat, or a hollow, empty feeling; the blues can settle deep in the body",
      systemNote: "The blues can weigh down your body like a wet coat you've forgotten you're wearing.",
      emotionalDetail: "the tearfulness, the withdrawal, or the sense that joy is far away",
      bodyTension: "heavy chest, tired limbs, throat lump, hollow gut",
      emotionalList: "sadness, heaviness, withdrawal, loneliness",
    },
    t4: {
      subject: "what's feeding the sadness",
      storyIntro: "Bring up the story underneath the blues: the loss, the disappointment, the loneliness, or whatever has been weighing you down. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the story that triggers your blues",
    },
    t5: {
      gripPhrase: "lighten more of the weight the blues has been placing on you",
      storyReplay: "**replay** the story that triggers your blues from beginning to end",
      followup: "At this point, the blues may feel lighter this time.",
    },
    t6: {
      contextQuestion: "Does the weight feel lighter now? Does the situation feel less gray? Is your body softer when you picture it?",
      positiveVision: "Imagine what life looks like when the blues have lifted. Picture yourself smiling genuinely, laughing with someone you love, finding beauty in a simple moment. Maybe you're surrounded by a warm golden light that dissolves the grey, or you have wings that carry you above the heaviness into clear sky. Let this image be as warm and full of color as you can bring it.",
    },
    t7: {
      transformations: "Every heavy moment becomes a bright one, every tear becomes a laugh, every withdrawn afternoon becomes an open, connected one.",
      metaphor: "just like paint covering a wall, or a beautiful portrait covering old imperfections",
    },
    t8: {
      triggerExamples: "a quiet Sunday afternoon, a reminder of something you've lost, the end of a busy day, an anniversary of something painful",
    },
  },

  boost_self_worth: {
    theme: "the low self-worth",
    t3: {
      opener: "You chose yourself today — and that matters more than you know. Let's turn up your worth together.",
      bodyResponse: "a sinking in your chest, a slump in your shoulders, a downcast gaze, or a small, shrinking feeling in your body",
      systemNote: "Old stories about not being good enough get written right into how your body holds itself.",
      emotionalDetail: "the self-criticism, the comparison, or the voice that says 'you're not enough'",
      bodyTension: "sunken chest, slumped shoulders, downcast gaze, tight stomach",
      emotionalList: "shame, self-doubt, inadequacy, sadness",
    },
    t4: {
      subject: "the voice of not-enough-ness",
      storyIntro: "Bring up the self-worth story: the inner critic, the moments that shaped it, the ways it speaks to you now. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the self-worth story",
    },
    t5: {
      gripPhrase: "quiet more of the inner critic's hold on how you see yourself",
      storyReplay: "**replay** the self-worth story from beginning to end",
      followup: "At this point, the inner critic's voice may feel quieter and less true this time.",
    },
    t6: {
      contextQuestion: "Does the critic feel quieter now? Does your worth feel a little less up for debate? Does your body stand a little taller?",
      positiveVision: "Imagine yourself walking through the world with quiet, rooted self-worth — taking up space without apology, speaking your truth, receiving love without deflecting it. Maybe you feel a warm, steady light glowing in your chest, or you see yourself standing tall in front of a mirror with easy, genuine love for the person looking back. Let this new picture of you be exactly as tall and grounded as you deserve.",
    },
    t7: {
      transformations: "Every 'not enough' becomes 'I am exactly enough,' every shrinking moment becomes a standing-tall one, every harsh inner voice becomes a kind, encouraging one.",
      metaphor: "just like replacing a cracked, clouded mirror with a clear, beautiful one",
    },
    t8: {
      triggerExamples: "looking in the mirror, receiving a compliment you want to deflect, comparing yourself on social media, a moment of failure or criticism",
    },
  },

  hold_boundaries: {
    theme: "the boundary challenge",
    t3: {
      opener: "Nice work taking this on! Learning to hold your line is one of the kindest things you'll ever do — for you.",
      bodyResponse: "a tightening in your chest, a sinking stomach, a rush of heat, or a shrinking feeling when you imagine saying no",
      systemNote: "Boundary-holding often triggers the same body-alarm as conflict, even when the stakes are small.",
      emotionalDetail: "the guilt, the fear of disappointing someone, or the urge to cave just to keep the peace",
      bodyTension: "tight chest, sinking stomach, flush of heat, shrinking posture",
      emotionalList: "guilt, fear, anger, resentment",
    },
    t4: {
      subject: "the boundary that needs holding",
      storyIntro: "Bring up the boundary situation: the person, the request, the moment where you need to say no, or hold your line, or protect your time, energy, or self. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the boundary story",
    },
    t5: {
      gripPhrase: "loosen more of the hold guilt and people-pleasing have on you",
      storyReplay: "**replay** the boundary moment from beginning to end",
      followup: "At this point, the boundary may feel less fraught and more matter-of-fact this time.",
    },
    t6: {
      contextQuestion: "Does holding your boundary feel less scary now? Does saying no feel less loaded? Is your body steadier when you picture it?",
      positiveVision: "Imagine yourself holding the boundary with calm, grounded strength — clear, kind, unmoved by guilt. Picture the other person's reaction without it shaking you. Maybe you see yourself as a steady tree with deep roots, or you feel an unshakeable warm center in your chest that can't be talked out of its own truth. Let this new picture of you be as steady and certain as a mountain.",
    },
    t7: {
      transformations: "Every wavering becomes steady, every caving becomes a clear no, every resentment becomes the quiet satisfaction of keeping your word to yourself.",
      metaphor: "just like replacing a paper wall with a solid, stone wall — nothing can push through anymore",
    },
    t8: {
      triggerExamples: "a certain person asking for a favor, a last-minute request, someone crossing a line, the urge to over-explain yourself",
    },
  },

  relief_from_grief: {
    theme: "the grief",
    t3: {
      opener: "You've taken an important and brave first step!",
      bodyResponse: "a heaviness in your chest, a lump in your throat, tears pressing behind your eyes, or a hollow space that never quite fills",
      systemNote: "Grief lives in the body — it comes in waves, often arriving without warning.",
      emotionalDetail: "the longing, the sorrow, or the ache of missing someone or something that was here and isn't now",
      bodyTension: "heavy chest, throat lump, tight shoulders, hollow gut",
      emotionalList: "sadness, longing, anger, tenderness",
    },
    t4: {
      subject: "the grief itself",
      storyIntro: "Bring up what or whom you're grieving — the loss, the memories, the moments that keep returning. Let them come gently, the way they want to come. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the grief story",
    },
    t5: {
      gripPhrase: "soften more of the sharpest edges of the grief without taking away the love underneath",
      storyReplay: "let the grief story move through you again",
      followup: "At this point, the grief may feel a little lighter, with more love and less sting this time.",
    },
    t6: {
      contextQuestion: "Does the grief feel a little softer now? Does the love shine through more clearly? Is your body more at peace as you remember?",
      positiveVision: "Imagine carrying the love without the sharpness of the loss — holding the good memories gently, smiling at what was, carrying it all inside you. Maybe you see a warm light in your chest where the love lives, or you feel the person or thing you've lost beside you in a peaceful way. Let this image be as tender or as expansive as feels true to you.",
    },
    t7: {
      transformations: "Every sharp edge softens into tenderness, every wave of pain becomes a wave of love, every empty moment becomes a moment of quiet remembering.",
      metaphor: "just like seeing leaves filling out tree limbs and branches in the spring — still there, but no longer visible",
    },
    t8: {
      triggerExamples: "a song that brings them back, an anniversary, a place you used to go together, a quiet evening when the missing gets loud",
    },
  },

  calm_your_anger: {
    theme: "the anger",
    t3: {
      opener: "Smart call! Turning toward the heat takes more strength than exploding ever could.",
      bodyResponse: "heat rising in your face, your jaw clenching, your fists tightening, or your chest tightening like a pressure cooker",
      systemNote: "Anger is fight-or-flight at full volume — your body is built to hold and fuel it.",
      emotionalDetail: "the frustration, the sense of injustice, or the urge to lash out",
      bodyTension: "hot face, clenched jaw, tight fists, tight chest",
      emotionalList: "frustration, rage, hurt, injustice",
    },
    t4: {
      subject: "what fired up the anger",
      storyIntro: "Bring up the anger story: the situation, the person, the moment where the heat rises and you feel the impulse to push back hard. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the anger story",
    },
    t5: {
      gripPhrase: "cool more of the heat so you can choose how to respond, not react",
      storyReplay: "**replay** the anger story from beginning to end",
      followup: "At this point, the heat may feel lower and your thinking clearer this time.",
    },
    t6: {
      contextQuestion: "Does the heat feel lower now? Does the situation feel less inflaming? Is your body cooler when you picture it?",
      positiveVision: "Imagine yourself meeting that same situation with calm, grounded power — clear, direct, even firm, but not consumed by heat. Picture yourself choosing your response. Maybe you feel a cool, steady blue light running down your spine, or you see yourself as a calm mountain that weather passes over without moving. Let this cooler picture be as steady and clear as feels right.",
    },
    t7: {
      transformations: "Every surge of heat becomes a steady pulse, every clenched fist becomes an open hand, every lashing word becomes a clear, chosen one.",
      metaphor: "just like letting a roaring fire settle into warm, useful coals",
    },
    t8: {
      triggerExamples: "a specific person's tone of voice, getting cut off in traffic, being ignored, a news story, a moment when something feels unfair",
    },
  },

  enhance_performance: {
    theme: "the performance pressure",
    t3: {
      opener: "Here's to the edge-seeker! You've got a fire for growth — let's channel it into true flow.",
      bodyResponse: "a tightness in your chest before the moment, jittery limbs, a quickened pulse, or that familiar pre-performance tension",
      systemNote: "Performance pressure sets off the same body response whether you're on stage, in a big meeting, or about to compete.",
      emotionalDetail: "the self-doubt, the fear of underperforming, or the pressure to prove yourself",
      bodyTension: "tight chest, jittery limbs, quick pulse, tense shoulders",
      emotionalList: "pressure, self-doubt, urgency, anticipation",
    },
    t4: {
      subject: "the performance situation",
      storyIntro: "Bring up the performance you want to sharpen: walking into the room, taking the field, starting the presentation, opening the pitch — whatever scene carries the weight. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the performance story",
    },
    t5: {
      gripPhrase: "unlock more of your ability to show up in the zone",
      storyReplay: "**replay** the performance story from beginning to end",
      followup: "At this point, the performance may feel more fluid and less loaded this time.",
    },
    t6: {
      contextQuestion: "Does the situation feel less pressurized now? Does your body feel looser when you picture it? Does the moment feel more like yours?",
      positiveVision: "Imagine yourself in complete flow — calm, focused, fully expressing your skill, reading the situation effortlessly, moving or speaking from trained instinct. Picture the audience, the competitors, or the room fading into background. Maybe you feel a cool clarity run through you, or you see yourself moving with the relaxed power of an athlete in the zone. Let this image feel as sharp, present, and dialed-in as your best moments.",
    },
    t7: {
      transformations: "Every doubt becomes focus, every tight muscle becomes ready readiness, every over-thinking moment becomes a flowing one.",
      metaphor: "just like replacing a heavy pack with a light, balanced one — same distance, completely different feel",
    },
    t8: {
      triggerExamples: "the days leading up to the event, walking into the venue, the last minute before the whistle or curtain, seeing the audience or opponents",
    },
  },

  new_habit_new_you: {
    theme: "the barrier to your new habit",
    t3: {
      opener: "You're building a new, healthier you, one small brave step at a time. Today counts.",
      bodyResponse: "a heavy reluctance to start, a sinking 'not today' feeling, tight shoulders that resist, or a sluggish pull away from the new routine",
      systemNote: "Your nervous system defaults to what's familiar — barriers to a new habit often show up as a body quietly saying 'no thanks' before your mind has even weighed in.",
      emotionalDetail: "the self-doubt, the 'I'm not ready' voice, or the excuse that forms before the choice is even fully yours",
      bodyTension: "heavy chest, tight shoulders, sluggish limbs, hesitant breath",
      emotionalList: "doubt, resistance, fatigue, discouragement",
    },
    t4: {
      subject: "the biggest barrier standing between you and your new habit",
      storyIntro: "Bring up the moment the barrier shows up: the alarm goes off and the new routine feels impossible, the craving hits and the healthier choice disappears, the excuse lands before you can catch it. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the barrier story",
    },
    t5: {
      gripPhrase: "loosen more of the grip this barrier has on your new habit",
      storyReplay: "**replay** the barrier story from beginning to end",
      followup: "At this point, the barrier may feel smaller and less convincing this time.",
    },
    t6: {
      contextQuestion: "Does the barrier feel smaller now? Does the new habit feel more reachable? Does your body lean toward it instead of away?",
      positiveVision: "Imagine yourself fully living the new healthy habit — you move into the new normal, the temptation shows up and passes right through you, the new choice feels natural, almost easy. Picture a future self who's been doing this for months — steadier energy, quiet pride, the kind of body and mind that come from following through. Maybe there's a warm glow in your chest each time you choose the new way. Let this image of the new you be as vivid and lived-in as you can make it.",
    },
    t7: {
      transformations: "Every excuse becomes a clear next step, every hesitation becomes a ready body, every barrier becomes an easy opening toward the new habit.",
      metaphor: "just like clearing a blocked path so you can walk straight to a beautiful, healthier life",
    },
    t8: {
      triggerExamples: "seeing someone mirroring your old habit, a stressful afternoon when you want to skip, a social event with old temptations, the tired end of a long day",
    },
  },

  beat_stage_fright: {
    theme: "the stage fright",
    t3: {
      opener: "Well look at you, stepping into the spotlight of your own healing! This takes guts — and you've clearly got them.",
      bodyResponse: "your heart racing, your hands trembling, your mouth going dry, or your stomach churning right before the moment",
      systemNote: "Stage fright hijacks your fight-or-flight system in a situation that's actually safe.",
      emotionalDetail: "the dread, the urge to flee, or the fear of being judged",
      bodyTension: "racing heart, trembling hands, dry mouth, churning stomach",
      emotionalList: "dread, panic, worry, fear of judgment",
    },
    t4: {
      subject: "the performance fear itself",
      storyIntro: "Bring up the performance story leading to your fear: walking up to the stage, facing an audience, beginning your presentation, or doing whatever it is you want or need to do in front of others. Choose a story that represents the 'first' or the 'worst' moment, situation, or circumstance creating this challenge for you. This is the story to focus on.",
      storyNoun: "the performance story",
    },
    t5: {
      gripPhrase: "loosen more of the grip stage fright has on your performance",
      storyReplay: "**replay** the performance story from beginning to end",
      followup: "At this point, the story may feel more fluid and less loaded this time.",
    },
    t6: {
      contextQuestion: "Does the audience feel less threatening now? Does the stage feel less distressing? Does your body stay calmer when you picture yourself performing?",
      positiveVision: "Imagine yourself performing brilliantly — walking confidently to the stage, speaking or performing with total ease, connecting with the audience, running the race or playing the game flawlessly, or receiving genuine appreciation. Picture yourself with an unshakeable calm, as if you've done this a thousand times. Maybe you're surrounded by a protective glow of confidence, or the audience is full of your biggest fans. Let this image be as bright and unshakeable as you want it to be.",
    },
    t7: {
      transformations: "Every shaking hand becomes steady and firm, every panicked thought becomes clear and focused, every feared failure becomes a personal best.",
      metaphor: "just like replacing old stained carpet with a beautiful, pristine new wood floor",
    },
    t8: {
      triggerExamples: "walking into a room full of people, seeing the microphone, all eyes turning to you, the moments before you speak, thinking about an upcoming competition",
    },
  },

  headache_relief: {
    theme: "the headache",
    t3: {
      opener: "You're here, and you're already doing the most important thing — turning toward the pain instead of bracing against it.",
      bodyResponse: "your jaw clenched, your shoulders pulled up toward your ears, your forehead pinched, your breath shallow, or a tight band around your head",
      systemNote: "A tension headache rarely lives in your head alone — it usually has accomplices in your neck, jaw, and shoulders.",
      emotionalDetail: "the frustration of yet another long day with pain, the helplessness, or the dread of it sticking around",
      bodyTension: "clenched jaw, tight forehead, pulled-up shoulders, shallow breath",
      emotionalList: "frustration, helplessness, dread, irritation",
    },
    t4: {
      subject: "what brought the headache on",
      storyIntro: "Bring up what you think set the headache off: the stressful conversation you couldn't shake, the deadline you've been bracing against, the disagreement that made your jaw clamp shut, or the screen you stared at too long.\n\nIf you don't know what caused the headache, don't worry. Simply focus on the body sensations associated with the pain. The ache in the back of your lower skull, the throbbing pain, neck tightness, the tension band, or anything else you sense. Choose a story that represents the 'first' or the 'worst' moment, situation, or sensations contributing to this headache. This is the story to focus on.",
      storyNoun: "the trigger story",
    },
    t5: {
      gripPhrase: "loosen more of the grip the trigger has on your body",
      storyReplay: "**replay** the triggering story from beginning to end",
      followup: "At this point, the tension in your jaw, forehead, and neck may feel a little softer.",
    },
    t6: {
      contextQuestion: "Does the headache feel lighter now? Does the trigger feel less consuming? Does your body stay softer when you picture it?",
      positiveVision: "Imagine a version of your body where the headache has fully released. Picture your forehead soft and cool, your temples spacious, your jaw loose, your neck and shoulders dropped away from your ears. See yourself moving through your day clear-headed, comfortable, and breathing easily. Maybe you're outside in fresh air, or curled up somewhere soft, or simply sitting still with nothing tight anywhere. Let this ease spread through you in whatever way brings the most relief.",
    },
    t7: {
      transformations: "Every clenched jaw becomes loose and easy, every tight forehead becomes smooth and cool, every pulled-up shoulder drops back to rest.",
      metaphor: "just like replacing old stained carpet with a beautiful, pristine new wood floor",
    },
    t8: {
      triggerExamples: "a stressful meeting on the calendar, a long screen day ahead, a clenched-jaw conversation, a poor night of sleep, skipping water or meals",
    },
  },

  nightmare_reset: {
    theme: "the nightmare",
    t3: {
      opener: "You're here — and just by turning toward the nightmare instead of away from it, you've already done the hardest part.",
      bodyResponse: "a fast heartbeat, shallow breath, a pit in your stomach, a tight chest, prickly skin, or a held breath you didn't realize you were holding",
      systemNote: "Nightmares dump real stress chemistry into the body — even though the danger isn't real, the response is.",
      emotionalDetail: "the lingering fear, the dread of going back to sleep, or the residue of helplessness from the dream",
      bodyTension: "fast heart, held breath, tight chest, prickly skin",
      emotionalList: "fear, dread, helplessness, grief",
    },
    t4: {
      subject: "the nightmare itself",
      storyIntro: "Gently bring up the nightmare. Treat it like a story your mind is replaying — not something happening to you now. You are safe, awake, and in the room you're in. Choose the version that represents the most intense moment, or the dream as a whole. This is the story to focus on.",
      storyNoun: "the dream",
    },
    t5: {
      gripPhrase: "loosen more of the nightmare's grip",
      storyReplay: "**replay** the dream from beginning to end",
      followup: "At this point, the imagery may feel dimmer and the fear behind it quieter.",
    },
    t6: {
      contextQuestion: "Does the nightmare feel less heavy now? Are the images dimmer, the fear quieter? Does your body stay softer when you picture it?",
      positiveVision: "Now let's give the dream a new story and ending — one you choose. Imagine the dream playing out, but this time you have whatever you need. Maybe you're flying away. Perhaps you are the superstrong one. Maybe a beloved person or a protective animal arrives and stands beside you. Maybe the scary figure shrinks to the size of a mouse while you tower over it, or the dark room fills with warm light, or you simply walk out a door and close it behind you. Maybe you wake up safe and warm and laugh at how absurd the whole thing was. Rewrite it any way that leaves you feeling safe and whole.",
    },
    t7: {
      transformations: "Every dark corner fills with light, every threat shrinks down to size, every helpless moment becomes one where you have exactly what you need.",
      metaphor: "just like replacing old stained carpet with a beautiful, pristine new wood floor",
    },
    t8: {
      triggerExamples: "climbing under the covers, turning out the light, the moment before sleep, waking in the middle of the night, hearing or seeing something that echoes the dream",
    },
  },

  fear_of_flying: {
    theme: "the flying fear",
    t3: {
      opener: "You're already doing the brave thing — turning toward the fear instead of avoiding the trip altogether.",
      bodyResponse: "your chest tightening, your stomach dropping, your palms going clammy, or a wave of dread climbing into your throat",
      systemNote: "Your nervous system has paired flying with danger and now floods you with alarm signals before the plane has even left the gate.",
      emotionalDetail: "the dread, the imagined worst-cases, or the feeling that you can't get off",
      bodyTension: "tight chest, dropped stomach, clammy hands, locked jaw",
      emotionalList: "dread, panic, helplessness, claustrophobia",
    },
    t4: {
      subject: "what scares you about flying",
      storyIntro: "Bring the fear up in detail: the take-off pressure, the turbulence, the closed cabin, the loss of control — whatever piece of flying triggers the strongest alarm for you. Choose a moment that represents the 'first' or the 'worst' time this fear gripped you, or a future flight that is weighing on you. This is the story to focus on.",
      storyNoun: "the flying story",
    },
    t5: {
      gripPhrase: "loosen more of the grip the fear has on your body before takeoff",
      storyReplay: "**replay** the flying scenario from boarding through landing",
      followup: "At this point, the cabin may feel less suffocating, the turbulence less catastrophic.",
    },
    t6: {
      contextQuestion: "Does the flight feel a little more survivable now? Are the worst-case scenes dimmer, the body softer when you picture sitting in that seat?",
      positiveVision: "Imagine yourself buckled into your seat with steady breath and a settled body. The engines hum, the plane lifts, the world tilts — and you stay calm and grounded. Maybe you're chatting with the person next to you, maybe you're listening to music with your eyes closed, maybe you simply notice that you can ride out turbulence like a boat riding a wave. Let this steadier picture be as smooth and unhurried as you like.",
    },
    t7: {
      transformations: "Every spike of dread becomes an easy breath, every tense moment becomes a settled one, every flight becomes a chapter you get through and come out the other side of.",
      metaphor: "just like swapping out a tangled, knotted rope for a smooth, gliding silk ribbon",
    },
    t8: {
      triggerExamples: "booking a flight, packing a bag, the morning of travel, the boarding announcement, the cabin door closing, the first bump of turbulence",
    },
  },

  cpap_claustrophobia: {
    theme: "the mask claustrophobia",
    t3: {
      opener: "Showing up for this is a real act of self-care — using your CPAP well changes everything about how you feel the next day.",
      bodyResponse: "your chest tightening, your breath becoming shallow, a sudden urge to rip the mask off, or a wave of panic rising as you exhale into it",
      systemNote: "Your nervous system has flagged the mask as a threat to your breathing — even though it's actually the opposite. We're going to teach it differently.",
      emotionalDetail: "the trapped feeling, the suffocating sensation, or the dread of climbing into bed and putting it on",
      bodyTension: "tight chest, shallow breath, clenched jaw, urge to push the mask away",
      emotionalList: "panic, trapped, smothered, dread",
    },
    t4: {
      subject: "what the mask feels like on your face",
      storyIntro: "Bring up the mask experience in detail: the seal pressing against your nose or mouth, the air pushing in, the straps around your head, the moment your body says 'I can't.' Choose a representation of the 'first' or the 'worst' time you panicked while wearing it. This is the story to focus on.",
      storyNoun: "the mask story",
    },
    t5: {
      gripPhrase: "loosen more of the squeeze the mask sensation has on your chest",
      storyReplay: "**replay** the mask experience from putting it on through falling asleep",
      followup: "At this point, the seal may feel less like a trap and more like a steady companion.",
    },
    t6: {
      contextQuestion: "Does the mask feel a little less suffocating now? Are the sensations more neutral, the breath easier when you picture wearing it?",
      positiveVision: "Imagine yourself slipping the mask on at bedtime with a calm body and easy breath. The air flows in, your lungs accept it, your shoulders sink into the pillow. Maybe you notice how rested you feel each morning, how clear-headed you are after a full night, how your partner stops worrying about your breathing. Maybe the mask just feels like part of the bedtime ritual now — boring, neutral, helpful. Let this new picture be as light and unencumbered as fresh, easy breath.",
    },
    t7: {
      transformations: "Every choking sensation becomes an easy breath, every panic spike becomes a settling sigh, every night with the mask becomes a night that actually restores you.",
      metaphor: "just like swapping a too-tight shirt for one that finally fits comfortably",
    },
    t8: {
      triggerExamples: "carrying the mask to bed, fitting the straps, the hum of the machine starting up, the first push of air, waking in the night with the mask still on",
    },
  },

  fear_of_small_places: {
    theme: "the closed-in fear",
    t3: {
      opener: "Look at you, facing this head-on. Tight spaces feel impossible until you build the body memory of getting through one calmly.",
      bodyResponse: "your chest tightening, your breath becoming shallow, the room seeming to shrink, or a sudden urgent need to get out",
      systemNote: "When your nervous system tags a small space as 'trapped,' it floods you with escape signals — even when you're completely safe.",
      emotionalDetail: "the trapped feeling, the urge to bolt, or the certainty that you'll lose control if you can't get out",
      bodyTension: "tight chest, shallow breath, racing pulse, hands clenched",
      emotionalList: "panic, trapped, suffocating, helpless",
    },
    t4: {
      subject: "what the small space feels like",
      storyIntro: "Bring it up in detail: the MRI tube closing around you, the elevator doors sliding shut, the crowded subway car, the small windowless room. Choose a representation of the 'first' or the 'worst' moment in a tight space — or a future appointment that is weighing on you. This is the story to focus on.",
      storyNoun: "the closed-in story",
    },
    t5: {
      gripPhrase: "loosen more of the squeeze the closed-in feeling has on your chest",
      storyReplay: "**replay** the tight-space scene from entering through leaving",
      followup: "At this point, the walls may feel less like they're closing in and more like an ordinary space you pass through.",
    },
    t6: {
      contextQuestion: "Does the small space feel a little more bearable now? Are the walls less menacing, the breath easier when you picture being inside?",
      positiveVision: "Imagine yourself stepping into that tight space with a calm body and steady breath. You know it's temporary, your nervous system knows you're safe, and your mind has somewhere else to be — maybe a memory of a wide open field, the rhythm of a song, the sound of someone you love. Maybe you ride out the MRI by counting breaths, the elevator ride feels boring instead of terrifying, the crowded room is just background. Let this new picture be as open, roomy, and yours as you'd like.",
    },
    t7: {
      transformations: "Every wave of panic becomes a steady breath, every shrinking room becomes a space you pass through, every small place becomes one you walk out of intact.",
      metaphor: "just like swapping a heavy weighted blanket for a light cotton sheet",
    },
    t8: {
      triggerExamples: "the appointment reminder, the elevator doors closing, the MRI technician asking you to stay still, a small windowless room, a crowded space where you can't easily move",
    },
  },

  new_experience_anxiety: {
    theme: "the new-experience anxiety",
    t3: {
      opener: "Good for you — most people just stay home. You're choosing to show up, and that already counts.",
      bodyResponse: "your stomach knotting, your heart fluttering, a hot flush across your face, or a strong urge to cancel and stay home",
      systemNote: "Your nervous system reads 'unfamiliar' as 'risk' — so it pre-loads alarm signals before you've even walked through the door.",
      emotionalDetail: "the imagined awkwardness, the self-consciousness, or the fear of not belonging once you're there",
      bodyTension: "fluttery chest, knotted stomach, hot face, tight throat",
      emotionalList: "self-consciousness, dread, awkwardness, wanting-to-flee",
    },
    t4: {
      subject: "the new experience that's coming up",
      storyIntro: "Bring it up in detail: the dance class you signed up for, the party invite you accepted, the group you keep almost joining. Picture walking in, not knowing anyone, all eyes briefly turning. Choose the moment or scene where the anxiety feels strongest. This is the story to focus on.",
      storyNoun: "the new-experience story",
    },
    t5: {
      gripPhrase: "loosen more of the grip the anticipation has on your body",
      storyReplay: "**replay** the new-experience scene from walking in through finding your footing",
      followup: "At this point, the moment of walking in may feel less spotlight-y, more ordinary.",
    },
    t6: {
      contextQuestion: "Does the walk-in feel less scary now? Are you a little more curious, a little less braced, when you picture yourself there?",
      positiveVision: "Imagine yourself walking into that new room with an easy body and a quietly curious mind. You smile, you say hi, you stand somewhere comfortable. Maybe someone offers a kind word, maybe you find a person you click with, maybe you simply enjoy being out of the house and around new energy. Maybe you dance — terribly or beautifully, it doesn't matter — because you came for *you*. Paint this new adventure with as much color and curiosity as you'd like.",
    },
    t7: {
      transformations: "Every twist of dread becomes a flicker of curiosity, every spotlight moment becomes background noise, every new room becomes one you can enter and leave on your own terms.",
      metaphor: "just like rolling up a stiff old rug to reveal a smooth, open floor underneath",
    },
    t8: {
      triggerExamples: "the calendar reminder the night before, getting dressed, the drive over, the moment you reach for the door handle, the first scan of faces in the room",
    },
  },

  resist_an_urge: {
    theme: "the urge",
    t3: {
      opener: "Showing up for this matters — every time you sit with an urge instead of acting on it, you change the pattern a little more.",
      bodyResponse: "a restless buzz in your hands, a pull in your chest, a craving that climbs from your belly into your throat, or a tense alert feeling in your whole body",
      systemNote: "An urge is your nervous system promising relief — but the relief lasts seconds, and the pattern stays. We're going to soften the urge itself so the choice gets easier.",
      emotionalDetail: "the restlessness, the 'just-one-won't-hurt' bargain, or the irritation when the urge doesn't get its way",
      bodyTension: "restless hands, tight chest, antsy legs, a pull toward the fridge / cigarette / vape / drink",
      emotionalList: "craving, restlessness, irritation, longing",
    },
    t4: {
      subject: "the urge as it shows up",
      storyIntro: "Bring the urge up in detail: the time of day it usually hits, the trigger that sets it off, the way your body leans toward the behavior. Choose a representative moment — late evening boredom, the cigarette break, the snack after a hard conversation. This is the story to focus on.",
      storyNoun: "the urge story",
    },
    t5: {
      gripPhrase: "loosen more of the pull the urge has on your body",
      storyReplay: "**replay** the urge scene from the first hint through riding it out",
      followup: "At this point, the urge may feel like a passing wave rather than an unbearable demand.",
    },
    t6: {
      contextQuestion: "Does the urge feel a little less commanding now? Is there a tiny bit more space between you and it when you picture the trigger moment?",
      positiveVision: "Imagine yourself in that trigger moment with a steady, settled body. The urge rises, you notice it, and you do something else — a glass of water, a walk around the block, a few breaths. The urge crests and falls without you. Maybe you feel quietly proud, maybe you feel relief at not having to clean up another regret, maybe you simply enjoy the feeling of being in charge again. Let this picture be as clear, confident, and free as it needs to be.",
    },
    t7: {
      transformations: "Every spike of craving becomes a wave you ride, every restless hand becomes a settled one, every trigger moment becomes a doorway you choose to walk past.",
      metaphor: "just like replacing a worn-out groove in the floor with a fresh, even surface",
    },
    t8: {
      triggerExamples: "the moment after a hard conversation, late-evening boredom, opening the pantry out of habit, the smoke break with coworkers, the sound of a wrapper or a lighter",
    },
  },
};

export default THEMES;
